import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Bot, Settings, MessageSquare, Map, Calculator, Clock, 
  Save, RefreshCw, Zap, Search, ShieldCheck, Mail, Target,
  ArrowRight, Home, Building, FileText, Activity
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BOT_METADATA = {
  land: { name: 'Land Acquisition', icon: Map, color: 'text-green-400' },
  section8: { name: 'Section 8 Hunter', icon: Target, color: 'text-blue-400' },
  sfh: { name: 'Wholesale SFH', icon: Home, color: 'text-orange-400' },
  multifamily: { name: 'Multi Family', icon: Building, color: 'text-purple-400' },
  portfolio_management: { name: 'Portfolio Management', icon: Clock, color: 'text-cyan-400' },
  accounting_tax: { name: 'Accounting & Tax', icon: Calculator, color: 'text-emerald-400' }
};

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const LegionBots = () => {
  const [bots, setBots] = useState([]);
  const [selectedBotId, setSelectedBotId] = useState('land');
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/legion/bots/profiles`);
        setBots(res.data.bots || []);
        loadConfig(selectedBotId);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const loadConfig = async (botId) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/bot-config/${botId}`);
      const data = res.data || {};
      if (!data.offer_logic) data.offer_logic = {};
      if (!data.geography) data.geography = { enabled_states: [] };
      if (!data.operational) data.operational = { start_hour: 9, end_hour: 18, max_daily_leads: 50 };
      if (!data.behavior) data.behavior = { auto_respond: true, typing_speed: 40 };
      
      setConfig(data);
      setHasChanges(false);
    } catch (err) {
      setConfig({
        bot_id: botId,
        offer_logic: {},
        geography: { enabled_states: [] },
        operational: { start_hour: 9, end_hour: 18, max_daily_leads: 50 },
        behavior: { auto_respond: true, typing_speed: 40 }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBotSelect = (botId) => {
    setSelectedBotId(botId);
    loadConfig(botId);
  };

  const updateNestedConfig = (section, key, value) => {
    setConfig(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value
      }
    }));
    setHasChanges(true);
  };

  const toggleState = (state) => {
    const currentStates = config?.geography?.enabled_states || [];
    const newStates = currentStates.includes(state)
      ? currentStates.filter(s => s !== state)
      : [...currentStates, state];
    updateNestedConfig('geography', 'enabled_states', newStates);
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/bot-config/${selectedBotId}`, config);
      toast.success('Agent logic synchronized.');
      setHasChanges(false);
    } catch (err) {
      toast.error('Sync failed.');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !config) return <div className="flex h-screen items-center justify-center font-bold">LOADING LOGIC...</div>;

  const meta = BOT_METADATA[selectedBotId] || { name: 'Unknown Bot', icon: Bot, color: 'text-primary' };
  const BotIcon = meta.icon;

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-8">
      <div className="flex items-center justify-between border-b pb-4">
        <div>
          <h1 className="text-2xl font-black uppercase tracking-tight">Agent Logic Center</h1>
          <p className="text-xs text-muted-foreground font-mono mt-1 uppercase">Threshold Tuning & Operation Windows</p>
        </div>
        {hasChanges && (
          <Button onClick={saveConfig} disabled={saving} className="bg-primary hover:bg-primary/90 font-bold h-10 px-6">
            {saving ? <RefreshCw className="animate-spin mr-2 h-4 w-4" /> : <Save className="mr-2 h-4 w-4" />}
            Sync Logic
          </Button>
        )}
      </div>

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-3 space-y-2">
          {bots.map((bot) => {
            const bMeta = BOT_METADATA[bot.bot_type] || { name: bot.name, icon: Bot };
            const BIcon = bMeta.icon;
            return (
              <button
                key={bot.bot_type}
                onClick={() => handleBotSelect(bot.bot_type)}
                className={`w-full p-4 rounded-xl text-left transition-all border-2 flex items-center gap-3 ${
                  selectedBotId === bot.bot_type 
                    ? 'bg-primary border-primary text-white shadow-lg' 
                    : 'bg-card border-border hover:border-primary/40'
                }`}
              >
                <BIcon size={18} />
                <p className="text-xs font-bold uppercase tracking-tight">{bMeta.name}</p>
              </button>
            );
          })}
        </div>

        <div className="col-span-9">
          <Card className="border-border shadow-xl bg-card overflow-hidden min-h-[500px]">
            <CardHeader className="bg-muted/20 border-b p-6">
              <div className="flex items-center gap-4">
                <div className={`p-2 rounded-lg bg-background border ${meta.color}`}>
                  <BotIcon size={24} />
                </div>
                <div>
                  <CardTitle className="text-xl font-black uppercase italic">{meta.name}</CardTitle>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <Tabs defaultValue="logic" className="w-full">
                <TabsList className="grid grid-cols-4 h-10 bg-muted p-1 mb-8">
                  <TabsTrigger value="logic" className="font-bold uppercase text-[10px]">Logic</TabsTrigger>
                  <TabsTrigger value="geography" className="font-bold uppercase text-[10px]">Geography</TabsTrigger>
                  <TabsTrigger value="behavior" className="font-bold uppercase text-[10px]">Behavior</TabsTrigger>
                  <TabsTrigger value="operational" className="font-bold uppercase text-[10px]">Timeline</TabsTrigger>
                </TabsList>

                <TabsContent value="logic" className="space-y-6">
                  <div className="grid grid-cols-2 gap-6">
                    {selectedBotId === 'land' ? (
                      <>
                        <div className="space-y-2">
                          <Label className="text-[10px] font-black uppercase text-muted-foreground">Offer Range (%)</Label>
                          <div className="grid grid-cols-2 gap-2">
                             <Input type="number" value={(config?.offer_logic?.offer_low_percent || 0.25) * 100} 
                               onChange={e => updateNestedConfig('offer_logic', 'offer_low_percent', parseFloat(e.target.value)/100)} />
                             <Input type="number" value={(config?.offer_logic?.offer_high_percent || 0.40) * 100} 
                               onChange={e => updateNestedConfig('offer_logic', 'offer_high_percent', parseFloat(e.target.value)/100)} />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[10px] font-black uppercase text-muted-foreground">Hard Cap (%)</Label>
                          <Input type="number" value={(config?.offer_logic?.max_percent_hard_cap || 0.65) * 100} 
                             onChange={e => updateNestedConfig('offer_logic', 'max_percent_hard_cap', parseFloat(e.target.value)/100)} />
                        </div>
                      </>
                    ) : selectedBotId === 'section8' ? (
                      <>
                        <div className="space-y-2">
                          <Label className="text-[10px] font-black uppercase text-muted-foreground">Max Down Payment ($)</Label>
                          <Input type="number" value={config?.offer_logic?.max_down_payment || 5000} 
                             onChange={e => updateNestedConfig('offer_logic', 'max_down_payment', parseInt(e.target.value))} />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[10px] font-black uppercase text-muted-foreground">Max Monthly P+I ($)</Label>
                          <Input type="number" value={config?.offer_logic?.max_monthly_payment || 600} 
                             onChange={e => updateNestedConfig('offer_logic', 'max_monthly_payment', parseInt(e.target.value))} />
                        </div>
                      </>
                    ) : selectedBotId === 'sfh' ? (
                      <>
                        <div className="space-y-2">
                          <Label className="text-[10px] font-black uppercase text-muted-foreground">ARV to MAO (%)</Label>
                          <Input type="number" value={(config?.offer_logic?.arv_to_mao_percent || 0.70) * 100} 
                             onChange={e => updateNestedConfig('offer_logic', 'arv_to_mao_percent', parseFloat(e.target.value)/100)} />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[10px] font-black uppercase text-muted-foreground">Min Profit Target ($)</Label>
                          <Input type="number" value={config?.offer_logic?.min_profit_target || 20000} 
                             onChange={e => updateNestedConfig('offer_logic', 'min_profit_target', parseInt(e.target.value))} />
                        </div>
                      </>
                    ) : selectedBotId === 'multifamily' ? (
                      <>
                        <div className="space-y-2">
                          <Label className="text-[10px] font-black uppercase text-muted-foreground">Min Units</Label>
                          <Input type="number" value={config?.offer_logic?.min_units || 16} 
                             onChange={e => updateNestedConfig('offer_logic', 'min_units', parseInt(e.target.value))} />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[10px] font-black uppercase text-muted-foreground">Target Cap Rate (%)</Label>
                          <Input type="number" value={(config?.offer_logic?.target_cap_rate || 0.07) * 100} 
                             onChange={e => updateNestedConfig('offer_logic', 'target_cap_rate', parseFloat(e.target.value)/100)} />
                        </div>
                      </>
                    ) : (
                      <div className="col-span-2 py-10 text-center opacity-30 uppercase font-black tracking-widest">Autonomous Logic Enabled</div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="geography">
                  <div className="grid grid-cols-10 gap-1">
                    {US_STATES.map(state => (
                      <Button
                        key={state}
                        size="sm"
                        variant={config?.geography?.enabled_states?.includes(state) ? "default" : "outline"}
                        onClick={() => toggleState(state)}
                        className={`h-8 text-[10px] font-bold ${config?.geography?.enabled_states?.includes(state) ? 'bg-primary' : ''}`}
                      >
                        {state}
                      </Button>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="behavior" className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-muted/20 rounded-xl border">
                    <Label className="text-xs font-black uppercase">Auto-Respond</Label>
                    <Switch checked={config?.behavior?.auto_respond !== false} onCheckedChange={v => updateNestedConfig('behavior', 'auto_respond', v)} />
                  </div>
                  <div className="space-y-2 p-4 bg-muted/20 rounded-xl border">
                    <div className="flex justify-between">
                      <Label className="text-xs font-black uppercase">Typing Speed</Label>
                      <span className="font-mono text-xs font-bold text-primary">{config?.behavior?.typing_speed || 40} WPM</span>
                    </div>
                    <Slider value={[config?.behavior?.typing_speed || 40]} min={20} max={100} step={5} onValueChange={([v]) => updateNestedConfig('behavior', 'typing_speed', v)} />
                  </div>
                </TabsContent>

                <TabsContent value="operational" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2 p-4 bg-muted/20 rounded-xl border">
                      <Label className="text-xs font-black uppercase">Quiet Hours (Start)</Label>
                      <Input type="number" value={config?.operational?.start_hour || 9} 
                        onChange={e => updateNestedConfig('operational', 'start_hour', parseInt(e.target.value))} />
                    </div>
                    <div className="space-y-2 p-4 bg-muted/20 rounded-xl border">
                      <Label className="text-xs font-black uppercase">Quiet Hours (End)</Label>
                      <Input type="number" value={config?.operational?.end_hour || 18} 
                        onChange={e => updateNestedConfig('operational', 'end_hour', parseInt(e.target.value))} />
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default LegionBots;
